/**
 * DocumentToolbar — Top toolbar using widgetr-lib components.
 *
 * Composes:
 * - widgetr-lib: Button, Dropdown, Badge, Input for toolbar controls
 * - snappr-lib: Snapshot creation (via context.snapshots)
 *
 * All toolbar elements map to widgetr-lib primitives.
 */

import { useState } from 'react';
import { useExpressrContext } from '../context/ExpressrContext.js';
import { DOCUMENT_THEMES } from './DocumentThemes.js';
import type { DocumentStatus } from '../types.js';

const STATUS_OPTIONS: { value: DocumentStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'review', label: 'Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

/**
 * DocumentToolbar renders the top bar for the active document.
 *
 * Would use from widgetr-lib:
 * - Input (for editable title)
 * - Dropdown (for status picker)
 * - Badge (for unsaved indicator)
 * - Button variants for Save, Snapshot, AI, History, Export, Docs
 */
export function DocumentToolbar() {
  const {
    activeDocument,
    state,
    config,
    updateDocument,
    saveDocument,
    snapshots,
    setSidePanel,
    setDocumentTheme,
  } = useExpressrContext();

  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  if (!activeDocument) {
    return (
      <div style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, opacity: 0.6 }}>
        Select or create a document to begin
      </div>
    );
  }

  const handleTitleSave = () => {
    if (titleValue.trim()) {
      updateDocument(activeDocument.id, { title: titleValue.trim() });
    }
    setEditingTitle(false);
  };

  const handleSnapshot = async () => {
    // Delegates to snappr-lib SnapshotManager
    try {
      await snapshots.createSnapshot(
        activeDocument.id,
        'expressr',
        `Snapshot: ${activeDocument.title}`,
        {
          tool: 'expressr',
          command: 'snapshot',
          author: config?.user?.name,
          errorsBeforeFix: 0,
          errorsAfterFix: 0,
          errorsFixed: 0,
          duration: 0,
          projectPath: '',
          branch: '',
        }
      );
    } catch {
      // snappr may not have git context — graceful fallback
      console.warn('Snapshot creation via snappr-lib failed, using local fallback');
    }
  };

  return (
    <div style={{ height: 48, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 8, borderBottom: '1px solid var(--border, #2a2a4a)' }}>
      {/* Title — would use widgetr Input */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
        {editingTitle ? (
          <input
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            onBlur={handleTitleSave}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleTitleSave();
              if (e.key === 'Escape') setEditingTitle(false);
            }}
            autoFocus
          />
        ) : (
          <span
            onClick={() => { setTitleValue(activeDocument.title); setEditingTitle(true); }}
            style={{ fontWeight: 600, cursor: 'pointer' }}
          >
            {activeDocument.title}
          </span>
        )}

        {/* Status — would use widgetr Dropdown */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowStatusMenu(!showStatusMenu)}>{activeDocument.status}</button>
          {showStatusMenu && (
            <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100 }}>
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { updateDocument(activeDocument.id, { status: opt.value }); setShowStatusMenu(false); }}
                  style={{ display: 'block', width: '100%' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Unsaved indicator — would use widgetr Badge */}
        {state.hasUnsavedChanges && <span style={{ fontSize: 10 }}>unsaved</span>}
        <span style={{ fontSize: 11, opacity: 0.6 }}>{activeDocument.wordCount} words</span>
      </div>

      {/* Action buttons — would use widgetr Button */}
      <button onClick={handleSnapshot}>Snapshot</button>
      <button onClick={saveDocument} disabled={state.saving}>
        {state.saving ? 'Saving...' : 'Save'}
      </button>

      {/* Theme selector — would use widgetr Dropdown */}
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setShowThemeMenu(!showThemeMenu)}
          style={{ fontSize: 12 }}
          title="Document theme"
        >
          {DOCUMENT_THEMES.find(t => t.id === state.documentThemeId)?.name ?? 'Theme'}
        </button>
        {showThemeMenu && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, zIndex: 100,
            background: 'var(--surface, #1a1a2e)', border: '1px solid var(--border, #2a2a4a)',
            borderRadius: 4, padding: '4px 0', minWidth: 180, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            {DOCUMENT_THEMES.map((theme) => (
              <button
                key={theme.id}
                onClick={() => { setDocumentTheme(theme.id); setShowThemeMenu(false); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '6px 12px', border: 'none', background: state.documentThemeId === theme.id ? 'rgba(108,108,240,0.15)' : 'transparent',
                  color: 'inherit', cursor: 'pointer', fontSize: 12,
                }}
              >
                <div style={{ fontWeight: 500 }}>{theme.name}</div>
                <div style={{ fontSize: 10, opacity: 0.5 }}>{theme.description}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      <span style={{ width: 1, height: 24, opacity: 0.2, background: 'currentColor' }} />

      <button onClick={() => setSidePanel(state.sidePanel === 'synthesis' ? 'none' : 'synthesis')}>AI</button>
      <button onClick={() => setSidePanel(state.sidePanel === 'reversr' ? 'none' : 'reversr')}>Edits</button>
      <button onClick={() => setSidePanel(state.sidePanel === 'snapshots' ? 'none' : 'snapshots')}>History</button>
      <button onClick={() => setSidePanel(state.sidePanel === 'export' ? 'none' : 'export')}>Export</button>
      <button onClick={() => setSidePanel(state.sidePanel === 'documents' ? 'none' : 'documents')}>Docs</button>
    </div>
  );
}